import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import { toast } from "react-toastify";
import UserContext from "./UserContext";
import ValetApi from "../api/Api";
import { getDateInputValueInTimeZone } from "../utility/dateInput";
import "./PaymentRevenueReportsPage.css";

function formatCents(cents, currency = "usd") {
    const value = Number(cents || 0) / 100;
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: String(currency || "usd").toUpperCase(),
        }).format(value);
    } catch {
        return `$${value.toFixed(2)}`;
    }
}

function formatPercent(value) {
    const normalized = Number(value || 0);
    if (!Number.isFinite(normalized)) return "0%";
    return `${Math.max(0, Math.min(100, normalized)).toFixed(1)}%`;
}

function getDefaultStartDate(todayIso) {
    const [year, month] = String(todayIso).split("-");
    return `${year}-${month}-01`;
}

const PaymentRevenueReportsPage = () => {
    const { userDetail } = useContext(UserContext);
    const roleId = Number(userDetail?.roleId || 0);
    const isAdmin = roleId >= 5;
    const userTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const todayIso = getDateInputValueInTimeZone(new Date(), userTimeZone);

    const [featureEnabled, setFeatureEnabled] = useState(null);
    const [isFeatureLoading, setIsFeatureLoading] = useState(false);
    const [selectedLocationId, setSelectedLocationId] = useState("all");
    const [startDate, setStartDate] = useState(getDefaultStartDate(todayIso));
    const [endDate, setEndDate] = useState(todayIso);
    const [rollupView, setRollupView] = useState("daily");
    const [isLoading, setIsLoading] = useState(false);
    const [report, setReport] = useState(null);

    const accessibleLocations = useMemo(() => {
        const rows = Array.isArray(userDetail?.accessibleLocations) ? userDetail.accessibleLocations : [];
        return rows
            .map((location) => ({
                id: Number(location.id),
                name: location.name || `Location ${location.id}`,
            }))
            .filter((location) => Number.isInteger(location.id) && location.id > 0);
    }, [userDetail]);

    const rollupRows = useMemo(() => {
        if (!report?.rollups) return [];
        const selected = report.rollups[rollupView];
        return Array.isArray(selected) ? selected : [];
    }, [report, rollupView]);

    useEffect(() => {
        let isMounted = true;

        async function fetchFeatureStatus() {
            if (!userDetail || roleId < 3) return;

            try {
                setIsFeatureLoading(true);
                const res = await ValetApi.getPaymentsFeatureStatus();
                if (isMounted) {
                    setFeatureEnabled(Boolean(res?.data?.featureEnabled));
                }
            } catch {
                if (isMounted) {
                    setFeatureEnabled(null);
                }
            } finally {
                if (isMounted) {
                    setIsFeatureLoading(false);
                }
            }
        }

        fetchFeatureStatus();

        return () => {
            isMounted = false;
        };
    }, [userDetail, roleId]);

    useEffect(() => {
        async function loadReport() {
            if (featureEnabled === false && !isAdmin) {
                setReport(null);
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const response = await ValetApi.getPaymentRevenueReport({
                    locationId: selectedLocationId === "all" ? null : Number(selectedLocationId),
                    startDate: startDate || null,
                    endDate: endDate || null,
                    timeZone: userTimeZone,
                });

                setReport(response?.data?.report || null);
            } catch (err) {
                setReport(null);
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load payment report", { autoClose: 1600 });
            } finally {
                setIsLoading(false);
            }
        }

        loadReport();
    }, [selectedLocationId, startDate, endDate, userTimeZone, featureEnabled, isAdmin]);

    const summary = report?.summary || {
        paymentsCount: 0,
        grossPaidCents: 0,
        cardPaidCents: 0,
        cashPaidCents: 0,
        voidCount: 0,
        voidWaivedCents: 0,
        otherPaidCents: 0,
    };

    const cardShare = summary.grossPaidCents > 0 ? (summary.cardPaidCents / summary.grossPaidCents) * 100 : 0;
    const cashShare = summary.grossPaidCents > 0 ? (summary.cashPaidCents / summary.grossPaidCents) * 100 : 0;

    const shouldHidePaymentReports = featureEnabled === false && !isAdmin;

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner payment-report-page">
                <div className="payment-report-header">
                    <h2>Payments Revenue Reports</h2>
                    <p>Daily real-time totals with weekly and monthly rollups for settlements by location and podium.</p>
                </div>

                {shouldHidePaymentReports ? <p>Payments are disabled in this environment. Payment reports are visible to Admin users only.</p> : null}

                {!shouldHidePaymentReports ? (
                    <>
                        <div className="payment-report-filters">
                            <div className="payment-report-filter-field">
                                <Label for="paymentReportLocation">Location Scope</Label>
                                <Input
                                    id="paymentReportLocation"
                                    type="select"
                                    value={selectedLocationId}
                                    onChange={(evt) => setSelectedLocationId(evt.target.value)}>
                                    <option value="all">All Accessible Locations</option>
                                    {accessibleLocations.map((location) => (
                                        <option
                                            key={location.id}
                                            value={String(location.id)}>
                                            {location.name}
                                        </option>
                                    ))}
                                </Input>
                            </div>

                            <div className="payment-report-filter-field">
                                <Label for="paymentReportStartDate">Start Date</Label>
                                <Input
                                    id="paymentReportStartDate"
                                    type="date"
                                    value={startDate}
                                    onChange={(evt) => setStartDate(evt.target.value)}
                                />
                            </div>

                            <div className="payment-report-filter-field">
                                <Label for="paymentReportEndDate">End Date</Label>
                                <Input
                                    id="paymentReportEndDate"
                                    type="date"
                                    value={endDate}
                                    onChange={(evt) => setEndDate(evt.target.value)}
                                />
                            </div>

                            <div className="payment-report-filter-field">
                                <Label>&nbsp;</Label>
                                <Button
                                    color="primary"
                                    type="button"
                                    disabled={isLoading}
                                    onClick={async () => {
                                        try {
                                            setIsLoading(true);
                                            const response = await ValetApi.getPaymentRevenueReport({
                                                locationId: selectedLocationId === "all" ? null : Number(selectedLocationId),
                                                startDate: startDate || null,
                                                endDate: endDate || null,
                                                timeZone: userTimeZone,
                                            });
                                            setReport(response?.data?.report || null);
                                        } catch (err) {
                                            toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to refresh payment report", { autoClose: 1600 });
                                        } finally {
                                            setIsLoading(false);
                                        }
                                    }}>
                                    {isLoading ? "Refreshing..." : "Refresh Now"}
                                </Button>
                            </div>
                        </div>

                        <div className="payment-report-summary-grid">
                            <div className="payment-report-summary-card">
                                <div className="payment-report-summary-label">Total Revenue</div>
                                <div className="payment-report-summary-value">{formatCents(summary.grossPaidCents)}</div>
                                <div className="payment-report-summary-sub">{summary.paymentsCount} total payments</div>
                            </div>
                            <div className="payment-report-summary-card">
                                <div className="payment-report-summary-label">Card Revenue</div>
                                <div className="payment-report-summary-value">{formatCents(summary.cardPaidCents)}</div>
                                <div className="payment-report-summary-sub">Share: {formatPercent(cardShare)}</div>
                            </div>
                            <div className="payment-report-summary-card">
                                <div className="payment-report-summary-label">Cash Revenue</div>
                                <div className="payment-report-summary-value">{formatCents(summary.cashPaidCents)}</div>
                                <div className="payment-report-summary-sub">Share: {formatPercent(cashShare)}</div>
                            </div>
                            <div className="payment-report-summary-card">
                                <div className="payment-report-summary-label">Voids</div>
                                <div className="payment-report-summary-value">{summary.voidCount}</div>
                                <div className="payment-report-summary-sub">Waived: {formatCents(summary.voidWaivedCents)}</div>
                            </div>
                        </div>

                        <div className="payment-report-share-bars">
                            <div className="payment-report-share-track">
                                <div
                                    className="payment-report-share-bar card"
                                    style={{ width: `${Math.max(2, cardShare)}%` }}
                                />
                            </div>
                            <div className="payment-report-share-meta">Card {formatPercent(cardShare)}</div>
                            <div className="payment-report-share-track">
                                <div
                                    className="payment-report-share-bar cash"
                                    style={{ width: `${Math.max(2, cashShare)}%` }}
                                />
                            </div>
                            <div className="payment-report-share-meta">Cash {formatPercent(cashShare)}</div>
                        </div>

                        <div className="payment-report-panel">
                            <div className="payment-report-panel-title">Revenue Type Totals</div>
                            <div className="payment-report-table-wrap">
                                <table className="payment-report-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Count</th>
                                            <th>Revenue</th>
                                            <th>Void Waived</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(report?.paymentTypeTotals || []).map((row) => (
                                            <tr key={row.paymentType}>
                                                <td>{row.paymentType}</td>
                                                <td>{row.paymentsCount}</td>
                                                <td>{formatCents(row.grossPaidCents)}</td>
                                                <td>{formatCents(row.voidWaivedCents)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="payment-report-grid-two">
                            <div className="payment-report-panel">
                                <div className="payment-report-panel-title">Revenue by Location</div>
                                <div className="payment-report-table-wrap">
                                    <table className="payment-report-table">
                                        <thead>
                                            <tr>
                                                <th>Location</th>
                                                <th>Revenue</th>
                                                <th>Card</th>
                                                <th>Cash</th>
                                                <th>Voids</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(report?.byLocation || []).map((row) => (
                                                <tr key={row.locationId}>
                                                    <td>{row.locationName}</td>
                                                    <td>{formatCents(row.grossPaidCents)}</td>
                                                    <td>{formatCents(row.cardPaidCents)}</td>
                                                    <td>{formatCents(row.cashPaidCents)}</td>
                                                    <td>{row.voidCount}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="payment-report-panel">
                                <div className="payment-report-panel-title">Revenue by Podium</div>
                                <div className="payment-report-table-wrap">
                                    <table className="payment-report-table">
                                        <thead>
                                            <tr>
                                                <th>Location</th>
                                                <th>Podium</th>
                                                <th>Revenue</th>
                                                <th>Card</th>
                                                <th>Cash</th>
                                                <th>Voids</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(report?.byPodium || []).map((row) => (
                                                <tr key={`${row.locationId}-${row.podiumId}`}>
                                                    <td>{row.locationName}</td>
                                                    <td>{row.podiumName}</td>
                                                    <td>{formatCents(row.grossPaidCents)}</td>
                                                    <td>{formatCents(row.cardPaidCents)}</td>
                                                    <td>{formatCents(row.cashPaidCents)}</td>
                                                    <td>{row.voidCount}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="payment-report-panel">
                            <div className="payment-report-panel-header-row">
                                <div className="payment-report-panel-title">Settlement Rollups</div>
                                <div className="payment-rollup-toggle-group">
                                    <Button
                                        color={rollupView === "daily" ? "primary" : "secondary"}
                                        size="sm"
                                        type="button"
                                        onClick={() => setRollupView("daily")}>
                                        Daily
                                    </Button>
                                    <Button
                                        color={rollupView === "weekly" ? "primary" : "secondary"}
                                        size="sm"
                                        type="button"
                                        onClick={() => setRollupView("weekly")}>
                                        Weekly
                                    </Button>
                                    <Button
                                        color={rollupView === "monthly" ? "primary" : "secondary"}
                                        size="sm"
                                        type="button"
                                        onClick={() => setRollupView("monthly")}>
                                        Monthly
                                    </Button>
                                </div>
                            </div>

                            <div className="payment-report-table-wrap">
                                <table className="payment-report-table">
                                    <thead>
                                        <tr>
                                            <th>Period Start</th>
                                            <th>Payments</th>
                                            <th>Total Revenue</th>
                                            <th>Card</th>
                                            <th>Cash</th>
                                            <th>Voids</th>
                                            <th>Waived</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rollupRows.map((row) => (
                                            <tr key={`${rollupView}-${row.periodStart}`}>
                                                <td>{row.periodStart ? new Date(row.periodStart).toLocaleDateString() : "--"}</td>
                                                <td>{row.paymentsCount}</td>
                                                <td>{formatCents(row.grossPaidCents)}</td>
                                                <td>{formatCents(row.cardPaidCents)}</td>
                                                <td>{formatCents(row.cashPaidCents)}</td>
                                                <td>{row.voidCount}</td>
                                                <td>{formatCents(row.voidWaivedCents)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>
        </section>
    );
};

export default PaymentRevenueReportsPage;
